import { useMemo, useRef } from 'react';

import { ClipUtils } from '@air/utils-clips';

import { STACK_VISIBLE_LIMIT } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentOnboardingAssetStack';
import { usePrivateImporterJobItemsList } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateImporterJobItemsList';
import {
  type PrivateWorkspaceAgentClipsListByIdsRows,
  usePrivateWorkspaceAgentClipsListByIds,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { buildFinishedImportBatch } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/buildFinishedImportBatch';
import {
  type FinishedImportTask,
  getLatestFinishedImportTask,
  type ImportBatchOfferTask,
  isFinishedImportTaskDismissedFromTray,
  rememberWatchedImportTasks,
} from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/getLatestFinishedImportTask';
import { type FinishedUploadBatch } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { makeImporterTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';

const EMPTY_CLIP_IDS: string[] = [];

const PREVIEW_CLIPS_PROCESSING_REFETCH_MS = 3_000;

const getPreviewClipsRefetchInterval = (rows: PrivateWorkspaceAgentClipsListByIdsRows | undefined) =>
  rows?.some((clip) => ClipUtils.isProcessing(clip.status)) ? PREVIEW_CLIPS_PROCESSING_REFETCH_MS : false;

type StableFinishedImportTask = {
  task: FinishedImportTask | null;
  workspaceId: string | undefined;
};

type ResolveFinishedImportTaskParams = {
  latest: FinishedImportTask | null;
  previous: StableFinishedImportTask;
  tasks: readonly ImportBatchOfferTask[];
  workspaceId: string | undefined;
};

const resolveFinishedImportTask = ({
  latest,
  previous,
  tasks,
  workspaceId,
}: ResolveFinishedImportTaskParams): FinishedImportTask | null => {
  const { task } = previous;

  if (!task || previous.workspaceId !== workspaceId) {
    return latest;
  }

  const isSameTask = latest?.taskId === task.taskId && latest.finishedAt === task.finishedAt;

  return isSameTask || (!latest && isFinishedImportTaskDismissedFromTray(task, tasks)) ? task : latest;
};

export const usePrivateFinishedImportBatch = (): { finishedImportBatch: FinishedUploadBatch | null } => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const tasksSelector = useMemo(() => makeImporterTasksSelectorByWorkspaceId(workspaceId), [workspaceId]);
  const importerTasks = useAirSelector(tasksSelector);
  const watchedTaskIdsRef = useRef(new Set<string>());
  const stableTaskRef = useRef<StableFinishedImportTask>({ task: null, workspaceId });

  const finishedTask = useMemo(() => {
    rememberWatchedImportTasks(watchedTaskIdsRef.current, importerTasks);
    const latest = getLatestFinishedImportTask(importerTasks, watchedTaskIdsRef.current);
    const next = resolveFinishedImportTask({
      latest,
      previous: stableTaskRef.current,
      tasks: importerTasks,
      workspaceId,
    });

    stableTaskRef.current = { task: next, workspaceId };

    return next;
  }, [importerTasks, workspaceId]);

  const { data: jobItems } = usePrivateImporterJobItemsList({
    importId: finishedTask?.taskId,
    type: 'assetVersion',
    workspaceId,
  });

  const previewClipIds = useMemo(() => {
    if (!jobItems) {
      return EMPTY_CLIP_IDS;
    }

    return jobItems
      .slice(0, STACK_VISIBLE_LIMIT)
      .flatMap((item) => (item.type === 'assetVersion' ? [item.assetVersionId] : []));
  }, [jobItems]);

  const { data: previewClips, isLoading: isPreviewLoading } = usePrivateWorkspaceAgentClipsListByIds(
    {
      clipIds: previewClipIds,
      workspaceId,
    },
    { refetchInterval: getPreviewClipsRefetchInterval },
  );

  const finishedImportBatch = useMemo(() => {
    // The pill waits for the stills so the stack does not open on blank cards. A failed fetch still offers.
    if (!finishedTask || !jobItems || isPreviewLoading) {
      return null;
    }

    return buildFinishedImportBatch({
      items: jobItems,
      previewClips: previewClips ?? [],
      task: finishedTask,
    });
  }, [finishedTask, isPreviewLoading, jobItems, previewClips]);

  return { finishedImportBatch };
};
